import { FC, useMemo, useCallback } from 'react'
import { PayCardConfirmPaneButton, PayCardMethodPaneButton } from 'components/features/pfm/Button/FloatingPaneButtons'
import { RangedDatePicker } from 'components/atoms/inputs/DatePicker/RangedDatePicker'
import { useFormContext } from 'react-hook-form'
import { useStoreSelector } from 'helpers/reactReduxHelpers'
import { currentCCLedgerSelector } from 'components/features/creditCard/creditCardStore/selectors'
import { getLocalTimeZone, parseDate } from '@internationalized/date'
import moment from 'moment'
import { formatUSDate } from 'helpers/dateTimeHelpers'
import Button from 'components/features/pfm/Button/Button'
import { DATE_FORMAT_ISO_FULL, DATE_FORMAT_READABLE_YEARLESS } from 'constants/datetime'
import Flex from 'components/atoms/Flex/Flex'
import { CCCreateCardPaymentRequest } from 'components/features/creditCard/creditCardStore/types'

const PayCardDateStep: FC<{}> = () => {
  const { register, setValue, watch, getFieldState } = useFormContext<CCCreateCardPaymentRequest>()
  const ledger = useStoreSelector(currentCCLedgerSelector)
  const dueDate = !!ledger?.payment_due_date ? parseDate(ledger.payment_due_date) : null
  const normalizedDate = dueDate?.toDate(getLocalTimeZone())
  const displayDate = formatUSDate(moment(normalizedDate), DATE_FORMAT_READABLE_YEARLESS)
  const selectedDate = watch('payment_date') as string
  const paymentMethod = watch('payment_method')
  const methodField = getFieldState('payment_method')
  const methodAlreadySelected = !!paymentMethod && methodField.isTouched

  const handleSetDate = useCallback((date) => {
    const myDate = date?.toDate(getLocalTimeZone())
    const result = formatUSDate(moment(myDate), DATE_FORMAT_ISO_FULL)
    setValue('payment_date', result)
  }, [setValue])

  const ClickableDueDate = useMemo(() => !!displayDate && (
    <Button
      onClick={ () => handleSetDate(dueDate) }
      style='asText'
      size='mini'
      theme='orange'
    >
      <strong>{ displayDate}</strong>
    </Button>
  ), [dueDate, displayDate, handleSetDate])

  const SubmitButton = useMemo(() => methodAlreadySelected ? (
    <PayCardConfirmPaneButton text='Save Payment Date' />
  ) : (
    <PayCardMethodPaneButton text='Save Payment Date' />
  ), [methodAlreadySelected])

  return (
    <Flex padding='large-responsive'>
      <section>
        <h2>Choose a date</h2>
        <p>
          Your payment due date is { ClickableDueDate }.
        </p>
        <p>
          Payments scheduled after the due date will be late and may incur a fee.
        </p>
      </section>
      <input type='hidden' { ...register('payment_date') } />
      <RangedDatePicker
        showCalendar
        label='Payment Date'
        value={ parseDate(selectedDate) }
        onChange={ handleSetDate }
        highlightedDates={ !!dueDate ? [dueDate] : undefined }
      />
      { SubmitButton }
      <small>
        To learn more about your payment due date, minimum payments and
        cancelations, please see the Consumer Credit Card Agreement.
      </small>
    </Flex>
  )
}

export default PayCardDateStep
